/**
 * oc-admin-store.ts
 * Zustand store for internal admin session state.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type OcInternalRole = "owner" | "admin" | "viewer";

export interface OcInternalUserSession {
  id: string;
  name: string;
  email: string;
  role: OcInternalRole;
  avatarUrl: string | null;
}

interface OcAdminSessionState {
  internalUser: OcInternalUserSession | null;
  /** Whether the session has been checked against the API */
  sessionChecked: boolean;
  setInternalUser: (user: OcInternalUserSession | null) => void;
  setSessionChecked: (checked: boolean) => void;
  clearSession: () => void;
}

export const useOcAdminStore = create<OcAdminSessionState>()(
  persist(
    (set) => ({
      internalUser: null,
      sessionChecked: false,
      setInternalUser: (user) => set({ internalUser: user }),
      setSessionChecked: (checked) => set({ sessionChecked: checked }),
      clearSession: () => set({ internalUser: null, sessionChecked: false }),
    }),
    {
      name: "oc-admin-session",
      partialize: (state) => ({ internalUser: state.internalUser }),
    },
  ),
);

// ---------------------------------------------------------------------------
// Derived selectors (used with useOcAdminStore)
// ---------------------------------------------------------------------------

/** Returns true if the current user may perform owner-only actions */
export function selectIsOwner(state: OcAdminSessionState): boolean {
  return state.internalUser?.role === "owner";
}

/** Returns true if the user has write permissions (owner or admin) */
export function selectCanWrite(state: OcAdminSessionState): boolean {
  return state.internalUser?.role === "owner" || state.internalUser?.role === "admin";
}
